import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useInventories } from "@/lib/repo";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { formatDateTime } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { Plus, ChevronRight, ScanLine } from "lucide-react";

export default function Inventory() {
  const navigate = useNavigate();
  const list = useInventories();
  const loading = false;
  const active = list.find((i) => i.status === "in_progress") || null;

  const history = list.filter((i)=>i.status!=="in_progress");

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between gap-3">
        <div>
          <div className="text-[10.5px] font-mono uppercase tracking-[0.14em] text-muted-foreground">Sesiones de conteo</div>
          <h1 className="font-editorial text-[32px] sm:text-[40px] leading-[1.05] tracking-tight mt-0.5">Inventarios</h1>
          <p className="text-[13px] text-muted-foreground mt-1">Iniciá una sesión y escaneá chips para marcar presentes.</p>
        </div>
        <Button asChild data-testid="inventory-new-button">
          <Link to="/inventario/nuevo"><Plus size={16}/>Iniciar</Link>
        </Button>
      </div>

      {active && (
        <Card className="border-primary/40 bg-accent/40">
          <CardHeader className="flex flex-row items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-primary animate-pulse" aria-hidden />
                <CardTitle className="text-[16px] text-accent-foreground tracking-tight">Sesión activa</CardTitle>
              </div>
              <p className="text-[12px] text-muted-foreground mt-1">
                {active.name || "Inventario"} · <span className="font-mono tabular-nums">{formatDateTime(active.started_at)}</span>
              </p>
            </div>
            <Button onClick={()=>navigate(`/inventario/${active.id}/sesion`)} data-testid="inventory-resume-button">
              <ScanLine size={14}/>Continuar
            </Button>
          </CardHeader>
        </Card>
      )}

      <Card>
        <CardHeader>
          <div className="text-[10.5px] font-mono uppercase tracking-[0.12em] text-muted-foreground">Historial</div>
          <CardTitle className="text-[17px] mt-0.5">Sesiones cerradas</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading && (
            <div className="p-4 space-y-2">
              {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-16 w-full rounded-[calc(var(--radius)-0.35rem)]" />)}
            </div>
          )}
          {!loading && history.length === 0 && (
            <div data-testid="empty-inventory" className="text-center py-10 px-5">
              <h3 className="font-editorial text-[22px] tracking-tight">Listo para inventariar</h3>
              <p className="text-sm text-muted-foreground mt-1.5 max-w-sm mx-auto">Elegí finca y lote para empezar a escanear chips RFID.</p>
              <Button asChild className="mt-5"><Link to="/inventario/nuevo">Iniciar inventario</Link></Button>
            </div>
          )}
          <div className="divide-hairline">
            {!loading && history.map((inv) => (
              <Link
                key={inv.id}
                to={`/inventario/${inv.id}`}
                className="flex items-center justify-between gap-3 px-4 sm:px-5 py-3.5 hover:bg-muted transition-colors duration-150 ring-focus"
                data-testid="inventory-row"
              >
                <div className="min-w-0">
                  <div className="text-[14.5px] font-heading font-semibold text-foreground tracking-tight">{inv.name || "Inventario"}</div>
                  <div className="text-[10.5px] font-mono text-muted-foreground tabular-nums mt-0.5">
                    {formatDateTime(inv.started_at)} · {inv.status === "finished" ? "Cerrado" : "Cancelado"}
                  </div>
                  <div className="mt-1.5 flex items-center gap-1.5">
                    <Badge variant="present" className="font-mono tabular-nums">{inv.scanned_animal_ids?.length || 0} pres</Badge>
                    <Badge variant="missing" className="font-mono tabular-nums">{inv.missing_animal_ids?.length || 0} falt</Badge>
                  </div>
                </div>
                <ChevronRight size={16} className="text-muted-foreground/60 shrink-0"/>
              </Link>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
